import React, { useState, useEffect, useContext } from 'react';
import { InspectionItemsClient, CategorysClient } from '../web-api-client.ts';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import BackButton from './BackButton.js';
import Loader from './Loader.js';
import UserContext from '../providers/UserContext.js';

export function InspectionItems() {
  const [inspectionitems, setInspectionItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingInspectionItem, setEditingInspectionItem] = useState(null);

  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const user = useContext(UserContext);
  // Defensive guard — nav link is already hidden for non-admins, but
  // technicians could still hit this URL directly. Server-side hardening
  // of /api/InspectionItems mutations is recommended next.
  const isDealerAdmin = !!(user?.roles && user.roles.includes('Dealer Admin'));

  // Keep categoryId empty string until user selects one (better UX than defaulting to 0)
  // Note: sortOrder is intentionally omitted — the server auto-assigns it on create.
  const [newInspectionItem, setNewInspectionItem] = useState({
    name: '',
    categoryId: '',
    requiresPhoto: false,
    description: '',
    statusLabelGreen: 'Good',
    statusLabelYellow: 'Fair',
    statusLabelRed: 'Poor',
  });

  useEffect(() => {
    populateInspectionItemData();
    populateCategories();
  }, []);

  async function populateInspectionItemData() {
    const client = new InspectionItemsClient();
    const data = await client.getInspectionItemList(user.dealerId);
    setInspectionItems(data);
    setLoading(false);
  }

  async function populateCategories() {
    const catClient = new CategorysClient();
    const list = await catClient.getCategoryList(user.dealerId);
    // Expecting [{ id, name }, ...]
    setCategories(list || []);
    setCategoriesLoading(false);
  }

  function handleEditClick(inspectionitem) {
    setEditingInspectionItem({
      id: inspectionitem.id ?? 0,
      categoryId: inspectionitem.categoryId ?? '',
      name: inspectionitem.name ?? '',
      requiresPhoto: Boolean(inspectionitem.requiresPhoto),
      description: inspectionitem.description ?? '',
      statusLabelGreen: inspectionitem.statusLabelGreen ?? 'Good',
      statusLabelYellow: inspectionitem.statusLabelYellow ?? 'Fair',
      statusLabelRed: inspectionitem.statusLabelRed ?? 'Poor',
      sortOrder: inspectionitem.sortOrder ?? '',
    });
    setShowModal(true);
  }

  async function handleDeleteClick(inspectionitemToDelete) {
    const client = new InspectionItemsClient();
    await client.deleteInspectionItem(inspectionitemToDelete.id);
    setInspectionItems(inspectionitems.filter(p => p.id !== inspectionitemToDelete.id));
  }

  async function handleModalSubmit() {
    if (!editingInspectionItem) return;
    const client = new InspectionItemsClient();
    const payload = normalizeDto(editingInspectionItem);

    const updated = await client.updateInspectionItem(payload.id, payload);
    setInspectionItems(items => items.map(i => (i.id === updated.id ? updated : i)));
    setShowModal(false);
  }

  async function handleFormSubmit(event) {
    event.preventDefault();
    const client = new InspectionItemsClient();
    const payload = normalizeDto(newInspectionItem);

    const created = await client.createInspectionItem(payload);
    setInspectionItems([...inspectionitems, created]);

    setNewInspectionItem({
      name: '',
      categoryId: '',
      requiresPhoto: false,
      description: '',
      statusLabelGreen: 'Good',
      statusLabelYellow: 'Fair',
      statusLabelRed: 'Poor',
    });
  }

  // Ensure types match the DTO the server expects
  // IMPORTANT: include ALL fields the server stores, otherwise PUT-style updates may null them.
  function normalizeDto(dto) {
    const categoryId =
      dto.categoryId === '' || dto.categoryId === null || dto.categoryId === undefined
        ? 0
        : Number.isFinite(Number(dto.categoryId))
          ? Number(dto.categoryId)
          : 0;

    const sortOrder =
      dto.sortOrder === '' || dto.sortOrder === null || dto.sortOrder === undefined
        ? 0
        : Number.isFinite(Number(dto.sortOrder))
          ? Number(dto.sortOrder)
          : 0;

    return {
      id: dto.id ?? 0,
      categoryId,
      name: dto.name ?? '',
      requiresPhoto: Boolean(dto.requiresPhoto),
      description: dto.description ?? '',

      // ✅ these were missing and were getting nulled on update
      statusLabelGreen: dto.statusLabelGreen ?? 'Good',
      statusLabelYellow: dto.statusLabelYellow ?? 'Fair',
      statusLabelRed: dto.statusLabelRed ?? 'Poor',
      sortOrder,
    };
  }

  // New form input handler
  function handleNewChange(e) {
    const { name, type, value, checked, valueAsNumber } = e.target;
    if (name === 'categoryId') {
      setNewInspectionItem(prev => ({ ...prev, categoryId: value === '' ? '' : Number(value) }));
      return;
    }
    setNewInspectionItem(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
            ? (Number.isNaN(valueAsNumber) ? '' : valueAsNumber)
            : value
    }));
  }

  // Edit modal input handler
  function handleEditChange(e) {
    const { name, type, value, checked, valueAsNumber } = e.target;
    if (name === 'categoryId') {
      setEditingInspectionItem(prev => ({ ...prev, categoryId: value === '' ? '' : Number(value) }));
      return;
    }
    setEditingInspectionItem(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
            ? (Number.isNaN(valueAsNumber) ? '' : valueAsNumber)
            : value
    }));
  }

  function getCategoryName(catId) {
    const c = categories.find(c => c.id === catId);
    return c ? c.name : '';
  }

  function renderInspectionItemsTable(inspectionitems) {
    return (
      <div>
        <BackButton />
        <h1 id="tableLabel">Edit Inspection Items</h1>
        <table className="table table-striped" aria-labelledby="tableLabel">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Requires Photo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inspectionitems.map(inspectionitem => (
              <tr key={inspectionitem.id}>
                <td>{inspectionitem.name}</td>
                <td>{getCategoryName(inspectionitem.categoryId) || inspectionitem.categoryId}</td>
                <td>{inspectionitem.requiresPhoto ? 'Yes' : 'No'}</td>
                <td>
                  <FontAwesomeIcon
                    icon={faPencilAlt}
                    role="button"
                    onClick={() => handleEditClick(inspectionitem)}
                  />
                  &nbsp;&nbsp;&nbsp;
                  <FontAwesomeIcon
                    icon={faTrash}
                    role="button"
                    onClick={() => handleDeleteClick(inspectionitem)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Edit Inspection Item</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingInspectionItem && (
              <form onSubmit={e => e.preventDefault()}>
                <div className="form-group mb-2">
                  <label>Name</label>
                  <input
                    name="name"
                    type="text"
                    className="form-control"
                    value={editingInspectionItem.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="form-group mb-2">
                  <label>Category</label>
                  <select
                    name="categoryId"
                    className="form-control"
                    value={
                      editingInspectionItem.categoryId === 0
                        ? ''
                        : editingInspectionItem.categoryId ?? ''
                    }
                    onChange={handleEditChange}
                    required
                    disabled={categoriesLoading}
                  >
                    <option value="" disabled>
                      {categoriesLoading ? 'Loading categories…' : 'Select a category'}
                    </option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group form-check mb-2">
                  <input
                    id="edit-requiresPhoto"
                    name="requiresPhoto"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!editingInspectionItem.requiresPhoto}
                    onChange={handleEditChange}
                  />
                  <label className="form-check-label" htmlFor="edit-requiresPhoto">
                    Requires Photo
                  </label>
                  <div className="form-text small">
                    Technicians must attach at least one photo to this item
                    before the inspection can be published. Photos and videos
                    can always be added regardless of this setting.
                  </div>
                </div>

                <div className="form-group mb-2">
                  <label>Help Text</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={3}
                    value={editingInspectionItem.description ?? ''}
                    onChange={handleEditChange}
                    placeholder="Descriptive text shown to the customer about this inspection item"
                  />
                </div>

                <div className="form-group mb-2">
                  <label>Status Label Green</label>
                  <input
                    name="statusLabelGreen"
                    value={editingInspectionItem.statusLabelGreen ?? ''}
                    onChange={handleEditChange}
                    placeholder="Status Label Green"
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group mb-2">
                  <label>Status Label Yellow</label>
                  <input
                    name="statusLabelYellow"
                    value={editingInspectionItem.statusLabelYellow ?? ''}
                    onChange={handleEditChange}
                    placeholder="Status Label Yellow"
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group mb-2">
                  <label>Status Label Red</label>
                  <input
                    name="statusLabelRed"
                    value={editingInspectionItem.statusLabelRed ?? ''}
                    onChange={handleEditChange}
                    placeholder="Status Label Red"
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group mb-4">
                  <label>Sort Order</label>
                  <input
                    name="sortOrder"
                    value={editingInspectionItem.sortOrder ?? ''}
                    onChange={handleEditChange}
                    placeholder="Sort Order"
                    className="form-control"
                    type="number"
                  />
                  <div className="form-text small">
                    Lower numbers appear first within the category. New items
                    are auto-numbered to the end — change this only if you want
                    to reorder.
                  </div>
                </div>
              </form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleModalSubmit}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  if (!isDealerAdmin) {
    return (
      <div className="container mt-4">
        <BackButton />
        <h1>Inspection Items</h1>
        <div className="alert alert-warning mt-3">
          You don't have permission to view this page. Dealer admin access is required.
        </div>
      </div>
    );
  }

  const contents = loading ? <Loader /> : renderInspectionItemsTable(inspectionitems);

  return (
    <div>
      {contents}

      <h3 className="mt-4">Add Inspection Item</h3>
      <form onSubmit={handleFormSubmit} className="mt-3" style={{ maxWidth: 640 }}>
        <div className="form-group mb-2">
          <label>Name</label>
          <input
            name="name"
            value={newInspectionItem.name}
            onChange={handleNewChange}
            placeholder="Inspection Item Name"
            required
            className="form-control"
          />
        </div>

        <div className="form-group mb-2">
          <label>Category</label>
          <select
            name="categoryId"
            className="form-control"
            value={newInspectionItem.categoryId === 0 ? '' : newInspectionItem.categoryId}
            onChange={handleNewChange}
            required
            disabled={categoriesLoading}
          >
            <option value="" disabled>
              {categoriesLoading ? 'Loading categories…' : 'Select a category'}
            </option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group form-check mb-2">
          <input
            id="new-requiresPhoto"
            name="requiresPhoto"
            type="checkbox"
            className="form-check-input"
            checked={!!newInspectionItem.requiresPhoto}
            onChange={handleNewChange}
          />
          <label className="form-check-label" htmlFor="new-requiresPhoto">
            Requires Photo
          </label>
          <div className="form-text small">
            Technicians must attach at least one photo to this item before the
            inspection can be published. Photos and videos can always be added
            regardless of this setting.
          </div>
        </div>

        <div className="form-group mb-2">
          <label>Help Text</label>
          <textarea
            name="description"
            className="form-control"
            rows={3}
            value={newInspectionItem.description}
            onChange={handleNewChange}
            placeholder="What is being inspected / guidance for the tech"
          />
        </div>

        <div className="form-group mb-2">
          <label>Status Label Green</label>
          <input
            name="statusLabelGreen"
            value={newInspectionItem.statusLabelGreen}
            onChange={handleNewChange}
            placeholder="Status Label Green"
            required
            className="form-control"
          />
        </div>

        <div className="form-group mb-2">
          <label>Status Label Yellow</label>
          <input
            name="statusLabelYellow"
            value={newInspectionItem.statusLabelYellow}
            onChange={handleNewChange}
            placeholder="Status Label Yellow"
            required
            className="form-control"
          />
        </div>

        <div className="form-group mb-2">
          <label>Status Label Red</label>
          <input
            name="statusLabelRed"
            value={newInspectionItem.statusLabelRed}
            onChange={handleNewChange}
            placeholder="Status Label Red"
            required
            className="form-control"
          />
        </div>

        {/* Sort Order is auto-assigned on the server (new items go to the end
            of their category). Admins can reorder later from the edit form. */}

        <button type="submit" className="btn btn-primary">Add Inspection Item</button>
      </form>
    </div>
  );
}

export default InspectionItems;
